import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

function createYearRange(start, end) {
    const yearRange = [];
    for (let year = start; year <= end; year++) {
        yearRange.push(year);
    }
    return yearRange;
}

function createMonthRange() {
    const monthRange = [];
    for (let month = 1; month <= 12; month++) {
        monthRange.push(month);
    }
    return monthRange;
}

// TICKET-01600: util.getWeeksInMonth() resolves each row's days from the ISO
// week NUMBER plus the calendar year, and only corrects the year for week 53.
// When 1. January falls on a Saturday or Sunday it belongs to week 52 of the
// PREVIOUS year (2000, 2006, 2011, 2012, 2017, 2022, 2023, 2028 ...), so the
// first row of January was rendered as week 52 of the same year, i.e. the end
// of December, and 1. January vanished from the grid entirely. For a date field
// the grid is the only way to enter a value (components/form/input.js renders
// no native input for type 'date'), so the day could not be picked at all.
//
// Build the rows from the Monday of the week holding each date instead; the ISO
// week number is then only a label, taken from the row's Thursday (the day that
// defines the ISO week, and the safest cell to read across time zones). Dates
// stay UTC midnight and the object keeps the same shape as before, so this is
// identical to util.getWeeksInMonth() for every month except the broken ones.
function buildWeeksInMonth(month, year) {
    const weeks = [];
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const monthEnd = Date.UTC(year, month - 1, lastDay);
    const monday = new Date(Date.UTC(year, month - 1, 1));
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));

    while (monday.getTime() <= monthEnd) {
        const daysInWeek = [];
        for (let i = 0; i < 7; i += 1) {
            const day = new Date(monday);
            day.setUTCDate(monday.getUTCDate() + i);
            daysInWeek.push(day);
        }
        const firstDay = daysInWeek[0];
        const lastDayInWeek = daysInWeek[6];
        const thursday = daysInWeek[3];
        weeks.push({
            start: firstDay.getUTCMonth() === month - 1 ? firstDay.getUTCDate() : 1,
            end: lastDayInWeek.getUTCMonth() === month - 1 ? lastDayInWeek.getUTCDate() : lastDay,
            week: util.getWeek(thursday),
            year: thursday.getUTCFullYear(),
            daysInWeek,
        });
        monday.setUTCDate(monday.getUTCDate() + 7);
    }
    return weeks;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_NO = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

class CalendarStore extends LocalModel {
    // persistDate: does this instance own the shared localStorage key
    // 'calendarDate' ("which day is the calendar view showing")? True for the
    // calendar view singleton exported below, FALSE for the per-field date
    // pickers in components/calendar/datePicker.js. TICKET-01503: those pickers
    // used to read and write the same key, so a date picked in one field (an
    // animal's birth date) became the day the next field's picker opened on
    // (the sale's forfallsdato) and moved the calendar view along with it.
    constructor({ persistDate = true } = {}) {
        super('calendar', {
            namePlural: 'calendars',
            namePluralReal: 'calendars',
            sort: 'sort',
            limit: 100,
            api: {
                search: {
                    url: '/api/calendars/',
                    params: {
                        limit: 15,
                        sort: 'name',
                    },
                },
                load: {
                    url: '/api/calendars/',
                    params: {},
                },
                save: {
                    url: '/api/calendars/',
                    params: {},
                },
                delete: {
                    url: '/api/calendars/',
                    params: {},
                },
            },
        });

        this.persistDate = persistDate;
        if (!persistDate) {
            // The @observable initializer already seeded calendarDate from the
            // shared key. A field picker must ignore it and start on today;
            // datePicker.loadAll() overrides this when the field has a value.
            this.calendarResetDate();
        }

        // Initialize isToday based on the loaded calendarDate
        this.checkToday();
        this.checkTodayWeek();
        try {
            this.getDays();
        } catch (e) {
            console.warn('Failed to initialize calendar days, resetting to day view:', e);
            this.viewName = 'day';
        }
    }

    @observable newCalendar = {};

    @observable calendar = {};

    @observable calendars = [];

    @observable availabilityCalendars = [];

    @observable clientHours = [];

    @observable totalHours = [];

    @observable allDaysInWeek = [];

    @observable allDaysInMonth = [];

    @observable allWeeksInMonth = [];

    @observable colors = [
        {
            color: '#202020',
            backgroundColor: '#0ab3e3',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#acdcd2',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#5dc3b3',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#fbac19',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#f36e21',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#f27684',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#4CAF50',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#2196F3',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#c185cc',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#E91E63',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#FFC107',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#FF5722',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#CDDC39',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#607D8B',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#795548',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        },
        {
            color: '#202020',
            backgroundColor: '#3F51B5',
            dragBackgroundColor: '#c0c0c0',
            borderColor: '#808080',
        }
    ];

    @observable viewName = (() => {
        const stored = util.get('calendarViewName');
        if (stored === 'week' || stored === 'month' || stored === 'day') {
            return stored;
        }
        return 'day';
    })();

    @observable showMonth = false;

    @observable calendarHours = [];

    @observable calendarDate = (() => {
        const storedDate = util.get('calendarDate');
        if (!storedDate) {
            return new Date();
        }

        try {
            const parsedDate = new Date(storedDate);

            // Check if date is valid
            if (isNaN(parsedDate.getTime())) {
                console.warn('Invalid stored calendarDate, using current date');
                return new Date();
            }

            // Check if date is within reasonable range (5 years past to 2 years future)
            const now = new Date();
            const fiveYearsAgo = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
            const twoYearsFromNow = new Date(now.getFullYear() + 2, now.getMonth(), now.getDate());

            if (parsedDate < fiveYearsAgo || parsedDate > twoYearsFromNow) {
                console.warn('Stored calendarDate out of reasonable range:', parsedDate, '- using current date');
                return new Date();
            }

            return parsedDate;
        } catch (error) {
            console.error('Error parsing calendarDate:', error);
            return new Date();
        }
    })();

    @observable calendarDateStart = new Date();

    @observable calendarDateEnd = new Date();

    @observable workhourStart = 8;

    @observable workhourEnd = 16;

    @observable startHour = 6;

    @observable endHour = 23;

    @observable isToday = true;

    @observable isTodayWeek = true;

    @observable dragging = false;

    @observable dragging = false;

    @observable startY = 0;

    @observable startYAbs = 0;

    @observable endY = 0;

    @observable selectionStartDate = null;

    @observable selectionEndDate = null;

    @observable selectionCalendarId = null;

    @observable eventId = null;

    @observable eventEnd = null;

    @observable isDraggingEvent = false;

    @observable draggedEventId = null;

    @observable draggedEventOriginalStart = null;

    @observable draggedEventDuration = null;

    @observable draggedEventOriginalCalendarId = null;

    @observable dragTargetCalendarId = null;

    @observable yearRange = createYearRange(1900, 2100);

    @observable monthRange = createMonthRange();

    @observable displayCalendarIds = (() => {
        const stored = util.get('calendarDisplayCalendarIds');
        if (Array.isArray(stored)) {
            return stored;
        }
        return [];
    })();

    @observable workweek = util.get('calendarWorkweek') === true;

    @observable compact = util.get('calendarCompact') === true;

    @observable showCancelled = util.get('calendarShowCancelled') === true;

    // Toolbar toggle (C shooting module): show conventions / trainings as
    // events in the calendar. Per-browser preference, ON until switched off.
    @observable showConventions = util.get('calendarShowConventions') !== false;

    // Toolbar toggle: in day/week view, hide calendars that have no turnus
    // (calendarAvailability row) on the day being shown. Per-browser
    // preference like showCancelled; the actual per-day test lives in
    // calendarAvailabilityStore.hasAvailability().
    @observable showOnlyActiveTurnus = util.get('calendarShowOnlyActiveTurnus') === true;

    filterCalendars = (calendar, index, array) => {
        if (this.displayCalendarIds.length === 0) {
            return true;
        }
        return this.displayCalendarIds.includes(calendar.id);
    }

    @action
    toggleCalendarDisplay(calendarId, calendarIds) {
        if (calendarIds) {
            this.displayCalendarIds = calendarIds;
        } else {
            if (this.displayCalendarIds.includes(calendarId)) {
                this.displayCalendarIds = this.displayCalendarIds.filter(id => id !== calendarId);
            } else {
                this.displayCalendarIds.push(calendarId);
            }
        }
        util.set('calendarDisplayCalendarIds', this.displayCalendarIds);
    }

    @action
    showAllCalendars() {
        this.displayCalendarIds = [];
        util.set('calendarDisplayCalendarIds', this.displayCalendarIds);
    }

    @action
    toggleWorkweek() {
        this.workweek = !this.workweek;
        util.set('calendarWorkweek', this.workweek);
    }

    @action
    toggleCompact() {
        this.compact = !this.compact;
        util.set('calendarCompact', this.compact);
    }

    @action
    toggleShowCancelled() {
        this.showCancelled = !this.showCancelled;
        util.set('calendarShowCancelled', this.showCancelled);
    }

    @action
    toggleShowConventions() {
        this.showConventions = !this.showConventions;
        util.set('calendarShowConventions', this.showConventions);
    }

    @action
    toggleShowOnlyActiveTurnus() {
        this.showOnlyActiveTurnus = !this.showOnlyActiveTurnus;
        util.set('calendarShowOnlyActiveTurnus', this.showOnlyActiveTurnus);
    }

    @action
    clearAll() {
        this.newCalendar = {};
        this.calendar = {};
        this.calendars = [];
        this.availabilityCalendars = [];
        this.clientHours = [];
        this.totalHours = [];
        this.calendarHours = [];
    }

    @computed
    get currentMonth() {
        return this.calendarDate.getMonth() + 1;
    }

    @computed
    get currentYear() {
        return this.calendarDate.getFullYear();
    }

    @action
    setStartHour(value) {
        // Customer settings may contain clock-style decimals (7.45 meaning 07:45).
        // All consumers expect whole hours, so round down to the containing hour.
        const num = parseFloat(value);
        this.startHour = Number.isFinite(num) ? Math.floor(num) : 6;
    }

    @action
    setEndHour(value) {
        // See setStartHour: round up so the intended end time is still covered.
        const num = parseFloat(value);
        this.endHour = Number.isFinite(num) ? Math.ceil(num) : 23;
    }

    @action
    setDragging(value) {
        this.dragging = value;
    }

    @action
    setStartY(value) {
        this.startY = value;
    }

    @action
    setStartYAbs(value) {
        this.startYAbs = value;
    }

    @action
    setEndY(value) {
        this.endY = value;
    }

    @action
    resetDragging() {
        this.dragging = false;
        this.startY = 0;
        this.startYAbs = 0;
        this.endY = 0;
        this.selectionStartDate = null;
        this.selectionEndDate = null;
    }

    @action
    checkToday() {
        const yyyy = this.calendarDate.getFullYear();
        const mm = this.calendarDate.getMonth() + 1;
        const dd = this.calendarDate.getDate();
        const today = new Date();
        const todayYyyy = today.getFullYear();
        const todayMm = today.getMonth() + 1;
        const todayDd = today.getDate();
        this.isToday = yyyy === todayYyyy && mm === todayMm && dd === todayDd;
    }

    @action
    checkTodayWeek() {
        this.isTodayWeek = this.calendarDate.toDateString() === new Date().toDateString();
    }

    // Only the calendar view remembers its date across reloads. Field pickers
    // (persistDate: false) keep their date on the instance only.
    persistCalendarDate() {
        if (this.persistDate === false) {
            return;
        }
        util.set('calendarDate', this.calendarDate.toISOString());
    }

    @action
    calendarResetDate() {
        this.calendarDate = new Date();
    }

    @action
    calendarPrev(callback = () => {}) {
        let days = 1;
        if (this.viewName === 'week') {
            days = 7;
        } else if (this.viewName === 'month') {
            days = 30;
        }
        const date = new Date(this.calendarDate);
        date.setDate(date.getDate() - days);
        this.calendarDate = date;
        this.persistCalendarDate();
        this.checkToday();
        this.getDays();
        callback();
    }

    @action
    calendarNext(callback = () => {}) {
        let days = 1;
        if (this.viewName === 'week') {
            days = 7;
        } else if (this.viewName === 'month') {
            days = 30;
        }
        const date = new Date(this.calendarDate);
        date.setDate(date.getDate() + days);
        this.calendarDate = date;
        this.persistCalendarDate();
        this.checkToday();
        this.getDays();
        callback();
    }

    @action
    calendarToday(callback = () => {}) {
        this.calendarDate = new Date();
        this.persistCalendarDate();
        this.checkToday();
        this.getDays();
        callback();
    }

    @action
    calendarSetDate(date, callback = () => {}) {
        this.calendarDate = new Date(date);
        this.persistCalendarDate();
        this.checkToday();
        this.getDays();
        callback();
    }

    @action
    calendarSetYear(year, callback = () => {}) {
        const date = new Date(this.calendarDate);
        date.setFullYear(year);
        this.calendarDate = date;
        this.persistCalendarDate();
        callback();
    }

    @action
    calendarSetMonth(month, callback = () => {}) {
        const date = new Date(this.calendarDate);
        date.setMonth(month);
        this.calendarDate = date;
        this.persistCalendarDate();
        callback();
    }

    @action
    calendarNextMonth(callback = () => {}) {
        const date = new Date(this.calendarDate);
        date.setMonth(date.getMonth() + 1);
        this.calendarDate = date;
        this.persistCalendarDate();
        callback();
    }

    @action
    calendarPrevMonth(callback = () => {}) {
        const date = new Date(this.calendarDate);
        date.setMonth(date.getMonth() - 1);
        this.calendarDate = date;
        this.persistCalendarDate();
        callback();
    }

    daysInMonth() {
        const date = new Date(this.calendarDate);
        const year = date.getFullYear();
        const month = date.getMonth();
        // return new Date(year, month + 1, 0).getDate();
        // return all days with date and day of week
        const days = [];
        const lastDay = new Date(year, month + 1, 0);
        const numDays = lastDay.getDate();
        for (let i = 1; i <= numDays; i += 1) {
            const day = new Date(year, month, i);
            days.push({
                day: day.getDate(),
                dayOfWeek: day.getDay(),
                nameOfDay: util.formatDate(day, { weekday: 'long', locale: 'nb-NO' }),
                date: day,
            });
        }
        return days;
    }

    getDays() {
        if (this.viewName === 'month') {
            this.getDaysInMonth();
        } else if (this.viewName === 'week') {
            this.getDaysInWeek();
        }
    }

    @action
    getDaysInMonth() {
        const days = this.daysInMonth();
        const weeks = this.weeksInMonth();
        this.allDaysInMonth = days;
        this.allWeeksInMonth = weeks;
    }

    @action
    getDaysInWeek() {
        // console.log('getDaysInWeek');
        const week = util.getWeek(this.calendarDate);
        const month = this.calendarDate.getMonth(); // 0-11
        let year = this.calendarDate.getFullYear();

        // Fix ISO week year calculation:
        // If week is 1 and month is December (11), the week belongs to next year
        // If week >= 52 and month is January (0), the week belongs to previous year
        if (week === 1 && month === 11) {
            year = year + 1;
        } else if (week >= 52 && month === 0) {
            year = year - 1;
        }

        const days = util.getDaysInWeek(week, year);
        this.allDaysInWeek = days;
    }

    weeksInMonth() {
        // Return all weeks in month
        const date = new Date(this.calendarDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const weeksInMonth = buildWeeksInMonth(month, year);
        const weeks = [];
        for (let i = 0, l = weeksInMonth.length ; i <= l; i += 1) {
            const week = weeksInMonth[i];
            weeks.push({
                month,
                week,
            });
        }
        return weeks;
    }

    @action
    calendarChangeView(viewName) {
        if (viewName === this.viewName) {
            this.viewName = 'day';
        } else {
            this.viewName = viewName;
        }
        util.set('calendarViewName', this.viewName);
        this.getDays();
    }

    @action
    toggleShowMonth() {
        this.showMonth = !this.showMonth;
    }

    @action
    getHoursInDay(date = this.calendarDate) {
        const hours = [];
        for (let i = this.startHour; i <= this.endHour; i += 1) {
            const hour = new Date(date);
            hour.setHours(i);
            hour.setMinutes(0);
            hour.setSeconds(0);
            hour.setMilliseconds(0);
            hours.push(hour);
        }
        this.calendarHours = hours;
    }

    @action
    setSelectionStartDate(date) {
        this.selectionStartDate = date;
    }

    @action
    setSelectionEndDate(date) {
        this.selectionEndDate = date;
    }

    @action
    setSelectionCalendarId(calendarId) {
        this.selectionCalendarId = calendarId;
    }

    @action
    setEventId(eventId) {
        this.eventId = eventId;
    }

    @action
    setEndOfEvent(eventEnd) {
        this.eventEnd = eventEnd;
    }

    @action
    setIsDraggingEvent(value) {
        this.isDraggingEvent = value;
    }

    @action
    setDraggedEventId(eventId) {
        this.draggedEventId = eventId;
    }

    @action
    setDraggedEventOriginalStart(startDate) {
        this.draggedEventOriginalStart = startDate;
    }

    @action
    setDraggedEventDuration(duration) {
        this.draggedEventDuration = duration;
    }

    @action
    setDraggedEventOriginalCalendarId(calendarId) {
        this.draggedEventOriginalCalendarId = calendarId;
    }

    @action
    setDragTargetCalendarId(calendarId) {
        this.dragTargetCalendarId = calendarId;
    }

    @action
    resetEventDrag() {
        this.isDraggingEvent = false;
        this.draggedEventId = null;
        this.draggedEventOriginalStart = null;
        this.draggedEventDuration = null;
        this.draggedEventOriginalCalendarId = null;
        this.dragTargetCalendarId = null;
    }

    getSelectionEvent(date, calendarId) {
        const start = new Date(date);
        const end = new Date(date);
        // Set end to 59 minutes and 59 seconds
        end.setMinutes(end.getMinutes() + 59);
        end.setSeconds(end.getSeconds() + 59);

        if (this.selectionStartDate >= start && this.selectionStartDate <= end && this.selectionCalendarId === calendarId) {
            return {
                start: this.selectionStartDate,
                end: this.selectionEndDate,
                calendarId,
            };
        }
        return null;
    }

    getDayName(dayOfWeek) {
        return DAYS[dayOfWeek];
    }

    getDayNameNo(dayOfWeek) {
        return DAYS_NO[dayOfWeek];
    }
}

const store = new CalendarStore();
export default store;

// Named export for the class itself
export { CalendarStore };
